import { RefillRows } from '../../components/invoices/RefillInvoiceSummary';
import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatNuf } from '../../utils/formatNuf';
import { formatCustomerName } from '../../utils/formatCustomerName';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { DataGrid, itIT } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  NoteAdd as CreditNoteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Payment as PaymentIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  TableChart as CsvIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import ResponsiveHeader from '../../components/ResponsiveHeader';
import ResponsiveFilters from '../../components/ResponsiveFilters';
import MobileCardList from '../../components/MobileCardList';
import { invoicesApi, exportApi, aziendeApi } from '../../services/api';
import { isCreditNote, isIssuedCreditNote, canCreateCreditNote, invoiceState } from '../../utils/invoiceDocument';
import { format, differenceInDays } from 'date-fns';
import BulkEmailDialog from '../../components/invoices/BulkEmailDialog';
import ExportPdfDialog from '../../components/invoices/ExportPdfDialog';
import SepaExportDialog from '../../components/invoices/SepaExportDialog';
import SendEmailDialog from '../../components/invoices/SendEmailDialog';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const formatNumber = (value, decimals = 0) => {
  return new Intl.NumberFormat('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy');
  } catch {
    return '-';
  }
};

const MONTH_OPTIONS = [
  { value: '1', label: 'Gennaio' },
  { value: '2', label: 'Febbraio' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Aprile' },
  { value: '5', label: 'Maggio' },
  { value: '6', label: 'Giugno' },
  { value: '7', label: 'Luglio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Settembre' },
  { value: '10', label: 'Ottobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Dicembre' },
];

const EMPTY_SUMMARY = {
  totalInvoiced: 0,
  totalPaid: 0,
  totalOutstanding: 0,
  invoiceCount: 0,
  outstandingCount: 0,
};

const roundAmount = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const summarizeInvoices = (invoices = []) => {
  return invoices.reduce((acc, inv) => {
    const total = parseFloat(inv.f_totale || 0) || 0;
    const paid = parseFloat(inv.f_paga_imp || 0) || 0;
    const outstanding = Math.max(0, total - paid);

    acc.totalInvoiced += total;
    acc.totalPaid += paid;
    acc.totalOutstanding += outstanding;
    acc.invoiceCount += 1;
    if (outstanding > 0.01) {
      acc.outstandingCount += 1;
    }

    return acc;
  }, { ...EMPTY_SUMMARY, totalInvoiced: 0, totalPaid: 0, totalOutstanding: 0, invoiceCount: 0, outstandingCount: 0 });
};

const SummaryTile = ({ label, value, color = 'text.primary' }) => (
  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} color={color}>
      {value}
    </Typography>
  </Paper>
);

const InvoiceList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [tabValue, setTabValue] = useState(initialFilter === 'overdue' ? 1 : 0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [billingType, setBillingType] = useState('');
  const [filterYears, setFilterYears] = useState([]);
  const [filterMonths, setFilterMonths] = useState([]);
  const [filterAzienda, setFilterAzienda] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deletePreviewError, setDeletePreviewError] = useState(null);

  // Stato ordinamento DataGrid
  const [sortModel, setSortModel] = useState([{ field: 'nuf', sort: 'desc' }]);

  const [sendSnackbar, setSendSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Stato dialog invio massivo email
  const [bulkEmailDialogOpen, setBulkEmailDialogOpen] = useState(false);
  
  // Stato dialog export PDF massivo
  const [exportPdfDialogOpen, setExportPdfDialogOpen] = useState(false);

  // Stato dialog export SEPA
  const [sepaDialogOpen, setSepaDialogOpen] = useState(false);
  
  // Stato dialog invio email singola
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);

  // Stato menu export CSV
  const [csvMenuAnchor, setCsvMenuAnchor] = useState(null);
  const [csvExporting, setCsvExporting] = useState(false);

  // Stato dialog export elettronico
  const [electronicDialogOpen, setElectronicDialogOpen] = useState(false);
  const [electronicNufFrom, setElectronicNufFrom] = useState('');
  const [electronicNufTo, setElectronicNufTo] = useState('');
  const [electronicExporting, setElectronicExporting] = useState(false);
  const [elFilterAzienda, setElFilterAzienda] = useState('');
  const [elFilterYear, setElFilterYear] = useState(new Date().getFullYear().toString());
  const [elFilterMonth, setElFilterMonth] = useState('');
  const [elInvoices, setElInvoices] = useState([]);
  const [elInvoicesLoading, setElInvoicesLoading] = useState(false);
  const [elSelectedIds, setElSelectedIds] = useState([]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilterYears([]);
    setFilterMonths([]);
    setFilterStatus('all');
    setBillingType('');
    setFilterAzienda('');
    setPage(0);
  };

  // Carica lista aziende per filtro
  const { data: aziendeData } = useQuery({
    queryKey: ['aziende'],
    queryFn: () => aziendeApi.getAll().then(res => res.data?.data || res.data || []),
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', { billingType, page, pageSize, search: searchQuery, status: filterStatus, years: filterYears.join(','), months: filterMonths.join(','), azienda_id: filterAzienda, tab: tabValue, sortModel }],
    queryFn: () => {
      if (tabValue === 1) {
        return invoicesApi.getOverdue({ billing_type: billingType || undefined }).then(res => ({
          invoices: res.data?.data || [], 
          total: res.data?.count || 0 
        }));
      }
      return invoicesApi.getAll({
        billing_type: billingType || undefined,
        page: page + 1,
        limit: pageSize,
        search: searchQuery,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        years: filterYears.length > 0 ? filterYears.join(',') : undefined,
        months: filterMonths.length > 0 ? filterMonths.join(',') : undefined,
        azienda_id: filterAzienda || undefined,
        sortBy: sortModel[0]?.field || 'nuf',
        sortOrder: sortModel[0]?.sort || 'desc',
      }).then(res => ({
        invoices: res.data?.data || [],
        summary: res.data?.summary || EMPTY_SUMMARY,
        total: res.data?.pagination?.total || 0
      }));
    },
    placeholderData: (prev) => prev
  });

  useEffect(() => {
    setPage(0);
  }, [searchQuery, filterStatus, filterAzienda, filterYears, filterMonths, tabValue]);

  const handleMenuOpen = (event, invoice) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await invoicesApi.generatePdf(selectedInvoice.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${isCreditNote(selectedInvoice) ? 'nota_credito' : 'fattura'}_${formatNuf(selectedInvoice.sezionale, selectedInvoice.nuf)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Errore download PDF:', error);
    }
    setMenuAnchor(null);
  };

  const handleSendEmail = async () => {
    if (!selectedInvoice?.id) return;
    setMenuAnchor(null);
    setSendEmailDialogOpen(true);
  };

  const deletePermanentMutation = useMutation({
    mutationFn: (invoiceId) => invoicesApi.deletePermanent(invoiceId),
    onSuccess: () => {
      for (const key of ['invoices', 'invoice', 'refillAvailable', 'customerInvoices', 'plantDeliveries']) queryClient.invalidateQueries({ queryKey: [key] });
      setDeleteDialogOpen(false);
      setDeletePreview(null);
    },
  });

  const cancelCreditMutation = useMutation({ mutationFn: invoiceId => invoicesApi.delete(invoiceId),
    onSuccess: () => { for (const key of ['invoices', 'invoice', 'creditNoteDraft']) queryClient.invalidateQueries({ queryKey: [key] }); },
    onError: error => setSendSnackbar({ open: true, severity: 'error', message: error.response?.data?.message || 'Errore nell’annullamento della nota' }) });

  const openDeleteDialog = async () => {
    if (!selectedInvoice?.id) return;
    setMenuAnchor(null);
    setDeleteDialogOpen(true);
    setDeletePreview(null);
    setDeletePreviewError(null);
    setDeletePreviewLoading(true);
    try {
      const res = await invoicesApi.getDeletePreview(selectedInvoice.id);
      setDeletePreview(res.data?.data || null);
    } catch (e) {
      setDeletePreviewError(e?.response?.data?.message || 'Errore nel caricamento anteprima cancellazione');
    } finally {
      setDeletePreviewLoading(false);
    }
  };

  // Handler Export CSV
  const handleCsvMenuOpen = (event) => {
    setCsvMenuAnchor(event.currentTarget);
  };

  const handleCsvMenuClose = () => {
    setCsvMenuAnchor(null);
  };

  const handleExportCsv = async (type) => {
    setCsvMenuAnchor(null);
    if (type === 'electronic') {
      setElectronicDialogOpen(true);
      return;
    }
    setCsvExporting(true);
    try {
      const params = {};
      if (filterYears.length > 0) params.years = filterYears.join(',');
      if (filterMonths.length > 0) params.months = filterMonths.join(',');
      if (filterStatus && filterStatus !== 'all') params.stato = filterStatus;
      
      let response;
      if (type === 'legacy') {
        response = await exportApi.exportInvoicesLegacy(params);
      } else if (type === 'electronic') {
        response = await exportApi.exportInvoicesElectronic(params);
      } else {
        response = await exportApi.exportInvoices(params);
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = type === 'legacy' 
        ? `FATTURE_${new Date().toISOString().split('T')[0]}.csv`
        : type === 'electronic'
        ? `FAT_ELETT_${new Date().toISOString().split('T')[0]}.csv`
        : `fatture_completo_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore export CSV:', error);
      setSendSnackbar({ open: true, message: 'Errore durante export CSV', severity: 'error' });
    } finally {
      setCsvExporting(false);
    }
  };

  // Carica fatture per dialog export elettronico
  const loadElInvoices = useCallback(async () => {
    setElInvoicesLoading(true);
    try {
      const params = { limit: 9999, sortBy: 'nuf', sortOrder: 'ASC' };
      if (elFilterYear) params.year = elFilterYear;
      if (elFilterMonth) params.month = elFilterMonth;
      if (elFilterAzienda) params.azienda_id = elFilterAzienda;
      if (electronicNufFrom) params.nufFrom = electronicNufFrom;
      if (electronicNufTo) params.nufTo = electronicNufTo;
      params.stato = 'emessa';
      const res = await invoicesApi.getAll(params);
      const list = res.data?.data || res.data?.invoices || [];
      setElInvoices(list);
      setElSelectedIds(list.map(i => i.id));
    } catch (e) {
      console.error('Errore caricamento fatture:', e);
      setElInvoices([]);
      setElSelectedIds([]);
    } finally {
      setElInvoicesLoading(false);
    }
  }, [elFilterYear, elFilterMonth, elFilterAzienda, electronicNufFrom, electronicNufTo]);

  useEffect(() => {
    if (electronicDialogOpen) {
      loadElInvoices();
    }
  }, [electronicDialogOpen, loadElInvoices]);

  const handleExportElectronic = async () => {
    if (elSelectedIds.length === 0) return;
    setElectronicExporting(true);
    try {
      const params = { ids: elSelectedIds.join(',') };

      // Scarica XLSX
      const csvResponse = await exportApi.exportInvoicesElectronic(params);
      const csvUrl = window.URL.createObjectURL(new Blob([csvResponse.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const csvLink = document.createElement('a');
      csvLink.href = csvUrl;
      csvLink.setAttribute('download', `FAT_ELETT_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(csvLink);
      csvLink.click();
      csvLink.remove();
      window.URL.revokeObjectURL(csvUrl);

      // Scarica Report PDF
      const pdfResponse = await exportApi.exportInvoicesElectronicReport(params);
      const pdfUrl = window.URL.createObjectURL(new Blob([pdfResponse.data], { type: 'application/pdf' }));
      const pdfLink = document.createElement('a');
      pdfLink.href = pdfUrl;
      pdfLink.setAttribute('download', `resoconto_fat_elett_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(pdfLink);
      pdfLink.click();
      pdfLink.remove();
      window.URL.revokeObjectURL(pdfUrl);

      setElectronicDialogOpen(false);
      setSendSnackbar({ open: true, message: 'Export XLSX e Report PDF scaricati', severity: 'success' });
    } catch (error) {
      console.error('Errore export elettronico:', error);
      setSendSnackbar({ open: true, message: 'Errore durante export fatture elettroniche', severity: 'error' });
    } finally {
      setElectronicExporting(false);
    }
  };

  const columns = [
    { field: 'billing_type', headerName: 'Tipologia', width: 155, sortable: false,
      renderCell: ({ value, row }) => <Chip size="small" label={isCreditNote(row) ? 'Nota di credito' : value === 'refill' ? 'Rifornimenti' : 'Contatore'} color={isCreditNote(row) ? 'info' : value === 'refill' ? 'primary' : 'default'} variant="outlined"/> },

    {
      field: 'nuf',
      headerName: 'Numero',
      width: 140,
      renderCell: (params) => (
        <Typography fontWeight="medium">
          {formatNuf(params.row.sezionale, params.value)}
        </Typography>
      ),
      sortComparator: (v1, v2, param1, param2) => {
        // Ordina prima per sezionale, poi per nuf numerico
        const sez1 = param1.api.getRow(param1.id)?.sezionale || '';
        const sez2 = param2.api.getRow(param2.id)?.sezionale || '';
        if (sez1 !== sez2) return sez1.localeCompare(sez2);
        return (v1 || 0) - (v2 || 0);
      },
    },
    {
      field: 'customer',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => {
        const customer = params.row.customer;
        if (customer) {
          return formatCustomerName(customer);
        }
        return params.row.f_nome || 'N/D';
      },
    },
    {
      field: 'f_emission',
      headerName: 'Data',
      width: 110,
      valueFormatter: (params) => params.value ? format(new Date(params.value), 'dd/MM/yyyy') : '',
    },
    {
      field: 'f_ult_gior',
      headerName: 'Scadenza',
      width: 110,
      renderCell: (params) => {
        if (!params.value) return '-';
        const dueDate = new Date(params.value);
        const daysOverdue = differenceInDays(new Date(), dueDate);
        const isPaid = params.row.stato === 'pagata';
        const isOverdue = daysOverdue > 0 && !isPaid && !isCreditNote(params.row) && params.row.stato !== 'annullata';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography color={isOverdue ? 'error' : 'inherit'}>
              {format(dueDate, 'dd/MM/yyyy')}
            </Typography>
            {isOverdue && <WarningIcon color="error" fontSize="small" />}
          </Box>
        );
      },
    },
    {
      field: 'f_totale',
      headerName: 'Totale',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'saldo_residuo',
      headerName: 'Saldo',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const saldo = parseFloat(params.value) || 0;
        return (
          <Typography color={saldo > 0 ? 'error.main' : 'success.main'} fontWeight="medium">
            {formatCurrency(saldo)}
          </Typography>
        );
      },
    },
    {
      field: 'azienda',
      headerName: 'Azienda',
      width: 150,
      valueGetter: (params) => params.row.azienda?.company_name || '-',
    },
    {
      field: 'stato',
      headerName: 'Stato',
      width: 155,
      renderCell: (params) => {
        const state = invoiceState(params.row);
        return <Chip label={state.label} size="small" color={state.color} />;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => (currentYear - i).toString());

  // Prepara righe con campi computati per mobile
  const invoiceRows = (data?.invoices || []).map(inv => {
    const customer = inv.customer;
    const customerName = customer ? formatCustomerName(customer) : (inv.f_nome || 'N/D');
    const saldo = parseFloat(inv.saldo_residuo) || 0;
    const dueDate = inv.f_ult_gior ? new Date(inv.f_ult_gior) : null;
    const daysOverdue = dueDate ? differenceInDays(new Date(), dueDate) : 0;
    const isPaid = inv.stato === 'pagata';
    const isOverdue = daysOverdue > 0 && !isPaid && !isCreditNote(inv) && inv.stato !== 'annullata';
    
    return {
      ...inv,
      customerName: isMobile ? customerName + (isCreditNote(inv) ? ' · Nota di credito' : inv.billing_type === 'refill' ? ' · Rifornimenti' : ' · Contatore') : customerName,
      formattedTotal: formatCurrency(inv.f_totale),
      formattedSaldo: formatCurrency(saldo),
      formattedDate: formatDate(inv.f_emission),
      formattedDue: formatDate(inv.f_ult_gior),
      isOverdue,
      effectiveStatus: invoiceState(inv).status,
      documentState: invoiceState(inv),
      saldoNum: saldo,
    };
  });

  const currentSummary = tabValue === 0
    ? (data?.summary || EMPTY_SUMMARY)
    : (() => {
        const localSummary = summarizeInvoices(data?.invoices || []);
        return {
          totalInvoiced: roundAmount(localSummary.totalInvoiced),
          totalPaid: roundAmount(localSummary.totalPaid),
          totalOutstanding: roundAmount(localSummary.totalOutstanding),
          invoiceCount: localSummary.invoiceCount,
          outstandingCount: localSummary.outstandingCount,
        };
      })();

  const renderYearsValue = (selected) => {
    if (!selected || selected.length === 0) return 'Tutti';
    const sorted = [...selected].sort((a, b) => Number(b) - Number(a));
    if (sorted.length <= 2) return sorted.join(', ');
    return `${sorted.length} anni`;
  };

  const renderMonthsValue = (selected) => {
    if (!selected || selected.length === 0) return 'Tutti';
    const labels = selected
      .map((value) => MONTH_OPTIONS.find((monthItem) => monthItem.value === value)?.label || value);
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.length} mesi`;
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 0 } }}>
      <ResponsiveHeader
        title="Fatture"
        subtitle="Gestione fatturazione clienti"
        primaryAction={{
          label: isMobile ? 'Nuova' : 'Nuova Fattura',
          icon: <AddIcon />,
          onClick: () => navigate('/crm/fatture/nuova'),
        }}
        secondaryActions={[
          { label: 'Export PDF', icon: <DownloadIcon />, onClick: () => setExportPdfDialogOpen(true) },
          { label: csvExporting ? 'Esportando...' : 'Export CSV', icon: <CsvIcon />, onClick: handleCsvMenuOpen, disabled: csvExporting },
          { label: 'Distinta SEPA', icon: <BankIcon />, onClick: () => setSepaDialogOpen(true) },
          { label: 'Invio Email Massivo', icon: <EmailIcon />, onClick: () => setBulkEmailDialogOpen(true) },
        ]}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField select size="small" label="Tipologia fattura" value={billingType} onChange={e => { setBillingType(e.target.value); setPage(0); }} sx={{ minWidth: 210 }}>
          <MenuItem value="">Tutte le tipologie</MenuItem><MenuItem value="meter">Da contatore</MenuItem><MenuItem value="refill">Da rifornimenti</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={() => navigate('/crm/fatture/nuova?billing_type=refill')}>Nuova da rifornimenti</Button>
      </Box>
      {/* Menu CSV rimane separato */}
      <Menu
        anchorEl={csvMenuAnchor}
        open={Boolean(csvMenuAnchor)}
        onClose={handleCsvMenuClose}
      >
        <MenuItem onClick={() => handleExportCsv('complete')}>
          <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
          Formato Completo
        </MenuItem>
        <MenuItem onClick={() => handleExportCsv('legacy')}>
          <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
          Formato Legacy (FoxPro)
        </MenuItem>
        <MenuItem onClick={() => handleExportCsv('electronic')}>
          <ListItemIcon><CsvIcon fontSize="small" /></ListItemIcon>
          Fattura Elettronica (Genioso)
        </MenuItem>
      </Menu>

      {/* Dialog Export Fattura Elettronica */}
      <Dialog open={electronicDialogOpen} onClose={() => setElectronicDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>Export Fattura Elettronica</DialogTitle>
        <DialogContent>
          {/* Filtri */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, mt: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel>Azienda</InputLabel>
              <Select value={elFilterAzienda} label="Azienda" onChange={(e) => setElFilterAzienda(e.target.value)}>
                <MenuItem value="">Tutte</MenuItem>
                {(aziendeData || []).map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.company_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Anno</InputLabel>
              <Select value={elFilterYear} label="Anno" onChange={(e) => setElFilterYear(e.target.value)}>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <MenuItem key={y} value={y.toString()}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Mese</InputLabel>
              <Select value={elFilterMonth} label="Mese" onChange={(e) => setElFilterMonth(e.target.value)}>
                <MenuItem value="">Tutti</MenuItem>
                {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((m, i) => (
                  <MenuItem key={i+1} value={(i+1).toString()}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" label="Da NUF" type="number" value={electronicNufFrom} onChange={(e) => setElectronicNufFrom(e.target.value)} sx={{ width: { xs: '100%', sm: 100 } }} />
            <TextField size="small" label="A NUF" type="number" value={electronicNufTo} onChange={(e) => setElectronicNufTo(e.target.value)} sx={{ width: { xs: '100%', sm: 100 } }} />
          </Box>

          {/* Seleziona tutte */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={elInvoices.length > 0 && elSelectedIds.length === elInvoices.length}
                  indeterminate={elSelectedIds.length > 0 && elSelectedIds.length < elInvoices.length}
                  onChange={(e) => setElSelectedIds(e.target.checked ? elInvoices.map(i => i.id) : [])}
                />
              }
              label={`Seleziona tutte (${elInvoices.length})`}
            />
            {elInvoicesLoading && <CircularProgress size={20} />}
          </Box>

          {/* Tabella fatture */}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, mt: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>NUF</TableCell>
                  <TableCell>Sez.</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell align="right">Totale</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {elInvoices.map(inv => (
                  <TableRow key={inv.id} hover sx={{ cursor: 'pointer' }} onClick={() => {
                    setElSelectedIds(prev => prev.includes(inv.id) ? prev.filter(id => id !== inv.id) : [...prev, inv.id]);
                  }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={elSelectedIds.includes(inv.id)} />
                    </TableCell>
                    <TableCell>{inv.nuf}</TableCell>
                    <TableCell>{inv.sezionale || '-'}</TableCell>
                    <TableCell>{inv.f_emission ? new Date(inv.f_emission).toLocaleDateString('it-IT') : '-'}</TableCell>
                    <TableCell>{inv.f_nome || '-'}</TableCell>
                    <TableCell align="right">{inv.f_totale != null ? `€ ${Number(inv.f_totale).toFixed(2)}` : '-'}</TableCell>
                  </TableRow>
                ))}
                {!elInvoicesLoading && elInvoices.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center">Nessuna fattura trovata</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Verranno scaricati il CSV per Genioso e il PDF resoconto con i totali.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setElectronicDialogOpen(false)} disabled={electronicExporting}>Annulla</Button>
          <Button
            onClick={handleExportElectronic}
            variant="contained"
            disabled={electronicExporting || elSelectedIds.length === 0}
            startIcon={electronicExporting ? <CircularProgress size={18} /> : <DownloadIcon />}
          >
            {electronicExporting ? 'Esportando...' : `Esporta (${elSelectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ mb: isMobile ? 1 : 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={isMobile ? 'Tutte' : 'Tutte le Fatture'} />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon color="error" fontSize="small" />
                Scadute
              </Box>
            } 
          />
        </Tabs>
        
        {tabValue === 0 && (
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <ResponsiveFilters
              searchValue={searchQuery}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Cerca cliente, n° fattura..."
              activeFilterCount={(filterYears.length > 0 ? 1 : 0) + (filterMonths.length > 0 ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0) + (filterAzienda ? 1 : 0)}
              onClearFilters={handleClearFilters}
            >
              <FormControl sx={{ minWidth: { xs: '100%', sm: 160 } }} size="small">
                <InputLabel>Anni</InputLabel>
                <Select
                  multiple
                  value={filterYears}
                  onChange={(e) => {
                    setFilterYears(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
                    setPage(0);
                  }}
                  label="Anni"
                  renderValue={renderYearsValue}
                >
                  {years.map(y => (
                    <MenuItem key={y} value={y}>
                      <Checkbox size="small" checked={filterYears.includes(y)} />
                      <Typography variant="body2">{y}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }} size="small">
                <InputLabel>Mesi</InputLabel>
                <Select
                  multiple
                  value={filterMonths}
                  onChange={(e) => {
                    setFilterMonths(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
                    setPage(0);
                  }}
                  label="Mesi"
                  renderValue={renderMonthsValue}
                >
                  {MONTH_OPTIONS.map((monthItem) => (
                    <MenuItem key={monthItem.value} value={monthItem.value}>
                      <Checkbox size="small" checked={filterMonths.includes(monthItem.value)} />
                      <Typography variant="body2">{monthItem.label}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }} size="small">
                <InputLabel>Stato</InputLabel>
                <Select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }} label="Stato">
                  <MenuItem value="all">Tutti</MenuItem>
                  <MenuItem value="pending">Da Pagare</MenuItem>
                  <MenuItem value="partial">Parziale</MenuItem>
                  <MenuItem value="paid">Pagata</MenuItem>
                  <MenuItem value="cancelled">Annullata</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: { xs: '100%', sm: 170 } }} size="small">
                <InputLabel>Azienda</InputLabel>
                <Select value={filterAzienda} onChange={(e) => { setFilterAzienda(e.target.value); setPage(0); }} label="Azienda">
                  <MenuItem value="">Tutte</MenuItem>
                  {(aziendeData || []).map(az => (
                    <MenuItem key={az.id} value={az.id}>{az.company_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ResponsiveFilters>
          </CardContent>
        )}
      </Card>

      <Card sx={{ mb: isMobile ? 1 : 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            {tabValue === 0 ? 'Riepilogo filtri' : 'Riepilogo fatture scadute'}
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' } }}>
            <SummaryTile label="Totale fatturato" value={formatCurrency(currentSummary.totalInvoiced)} color="primary.main" />
            <SummaryTile label="Totale pagato" value={formatCurrency(currentSummary.totalPaid)} color="success.main" />
            <SummaryTile label="Totale insoluto" value={formatCurrency(currentSummary.totalOutstanding)} color="error.main" />
            <SummaryTile label="Numero fatture" value={formatNumber(currentSummary.invoiceCount)} />
            <SummaryTile label="Fatture insolute" value={formatNumber(currentSummary.outstandingCount)} color="warning.main" />
          </Box>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>Errore nel caricamento</Alert>
      ) : isMobile ? (
        <MobileCardList
          rows={invoiceRows}
          loading={isLoading}
          onRowClick={(row) => navigate(`/crm/fatture/${row.id}`)}
          emptyMessage="Nessuna fattura trovata"
          pagination={tabValue === 0 ? {
            page,
            pageSize,
            total: data?.total || 0,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          } : undefined}
          renderCard={(inv) => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="subtitle2" noWrap sx={{ flex: 1, mr: 1 }}>
                    {inv.customerName}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold" color={inv.saldoNum > 0 ? 'error.main' : 'success.main'}>
                    {inv.formattedTotal}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">N° {formatNuf(inv.sezionale, inv.nuf)}</Typography>
                  <Typography variant="caption" color="text.secondary">|</Typography>
                  <Typography variant="caption" color="text.secondary">{inv.formattedDate}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {inv.isOverdue && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <WarningIcon color="error" sx={{ fontSize: 14 }} />
                      <Typography variant="caption" color="error">{inv.formattedDue}</Typography>
                    </Box>
                  )}
                  {inv.saldoNum > 0.01 && (
                    <Typography variant="caption" color="error.main">Saldo: {inv.formattedSaldo}</Typography>
                  )}
                  <Chip
                    label={inv.documentState?.label || {
                      'pagata': 'Pagata',
                      'parziale': 'Parziale',
                      'annullata': 'Annullata',
                      'emessa': 'Da Pagare',
                    }[inv.effectiveStatus] || 'Da Pagare'}
                    size="small"
                    color={{
                      'pagata': 'success',
                      'parziale': 'info',
                      'annullata': 'default',
                      'emessa': 'warning',
                    }[inv.effectiveStatus] || 'warning'}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              </Box>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, inv); }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        />
      ) : (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <DataGrid
              rows={data?.invoices || []}
              columns={columns}
              loading={isLoading}
              paginationMode={tabValue === 0 ? "server" : "client"}
              sortingMode={tabValue === 0 ? "server" : "client"}
              sortModel={sortModel}
              onSortModelChange={(newModel) => {
                setSortModel(newModel.length > 0 ? newModel : [{ field: 'nuf', sort: 'desc' }]);
                setPage(0);
              }}
              rowCount={data?.total || 0}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize); }}
              pageSizeOptions={[10, 25, 50, 100]}
              autoHeight
              disableRowSelectionOnClick
              localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
              sx={{ border: 'none' }}
              onRowClick={(params) => navigate(`/crm/fatture/${params.id}`)}
            />
          </CardContent>
        </Card>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); navigate(`/crm/fatture/${selectedInvoice?.id}`); }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>Visualizza
        </MenuItem>
        <MenuItem disabled={isIssuedCreditNote(selectedInvoice)} onClick={() => { setMenuAnchor(null); navigate(`/crm/fatture/${selectedInvoice?.id}/modifica`); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Modifica
        </MenuItem>
        {canCreateCreditNote(selectedInvoice) && <MenuItem onClick={() => { setMenuAnchor(null); navigate(`/crm/fatture/${selectedInvoice.id}/nota-credito`); }}>
          <ListItemIcon><CreditNoteIcon fontSize="small" /></ListItemIcon>Crea nota di credito
        </MenuItem>}
        <MenuItem onClick={handleDownloadPdf}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>Scarica PDF
        </MenuItem>
        <MenuItem onClick={handleSendEmail}>
          <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>Invia Email
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); navigate(isCreditNote(selectedInvoice) ? `/crm/fatture/${selectedInvoice.id}?payment=1` : `/crm/pagamenti?invoice=${selectedInvoice?.id}`); }}>
          <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>{isCreditNote(selectedInvoice) ? 'Registra rimborso' : 'Registra Pagamento'}
        </MenuItem>
        {isIssuedCreditNote(selectedInvoice) && selectedInvoice.stato !== 'annullata' && <MenuItem disabled={cancelCreditMutation.isPending} onClick={() => {
          setMenuAnchor(null);
          if (window.confirm('Annullare questa nota di credito? L’importo tornerà disponibile per un nuovo storno.')) cancelCreditMutation.mutate(selectedInvoice.id);
        }}>Annulla nota di credito</MenuItem>}
        <MenuItem onClick={openDeleteDialog}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>Elimina
        </MenuItem>
      </Menu>

      <Alert
        severity={sendSnackbar.severity}
        sx={{ display: sendSnackbar.open ? 'block' : 'none', mt: 2 }}
        onClose={() => setSendSnackbar({ open: false, message: '', severity: 'success' })}
      >
        {sendSnackbar.message}
      </Alert>

      {/* Dialog cancellazione definitiva */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletePermanentMutation.isPending && setDeleteDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {deletePreview?.warnings?.isCreditNote ? 'Elimina nota di credito definitivamente' : deletePreview?.invoice?.billing_type === 'refill' ? 'Elimina fattura da rifornimenti definitivamente' : 'Elimina fattura definitivamente'}
        </DialogTitle>
        <DialogContent dividers>
          {deletePreviewLoading && (
            <Box>
              <Skeleton height={28} />
              <Skeleton height={28} />
              <Skeleton height={200} />
            </Box>
          )}

          {!deletePreviewLoading && deletePreviewError && (
            <Alert severity="error">{deletePreviewError}</Alert>
          )}

          {!deletePreviewLoading && deletePreview && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {deletePreview.warnings?.isLinkedCreditNote ? 'La nota di credito verrà eliminata. Il relativo importo tornerà disponibile per uno storno della fattura originale.' : deletePreview.invoice?.billing_type === 'refill' ? 'La fattura e le sue righe verranno eliminate definitivamente. I rifornimenti originali saranno conservati e torneranno disponibili per una nuova fattura.' : 'Stai per eliminare definitivamente il documento e tutte le letture contatore collegate.'}
              </Typography>
              {deletePreview.warnings?.hasCreditNotes && <Alert severity="error" sx={{ mb: 2 }}>Elimina prima le note di credito collegate alla fattura.</Alert>}

              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="body2"><strong>NUF:</strong> {formatNuf(deletePreview.invoice?.sezionale, deletePreview.invoice?.nuf) || '-'}</Typography>
                  <Typography variant="body2"><strong>Data:</strong> {formatDate(deletePreview.invoice?.f_emission)}</Typography>
                  <Typography variant="body2"><strong>Cliente:</strong> {deletePreview.invoice?.f_nome || `${deletePreview.invoice?.customer?.nome || ''} ${deletePreview.invoice?.customer?.cognome || ''}`.trim() || 'N/D'}</Typography>
                  <Typography variant="body2"><strong>Totale:</strong> {formatCurrency(deletePreview.invoice?.f_totale)}</Typography>
                </Box>
              </Paper>

              {(deletePreview.warnings?.hasPayments || deletePreview.warnings?.hasAdvances) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {deletePreview.invoice?.billing_type === 'refill' ? 'Sono presenti pagamenti o acconti: rimuovili prima di eliminare definitivamente la fattura.' : 'Sono presenti pagamenti/acconti collegati: verranno eliminati insieme alla fattura.'}
                  {' '}
                  {deletePreview.totals?.payments_count
                    ? `(Pagamenti: ${deletePreview.totals.payments_count}, Totale: ${formatCurrency(deletePreview.totals.payments_total)})`
                    : ''}
                  {' '}
                  {deletePreview.totals?.advances_count
                    ? `(Acconti: ${deletePreview.totals.advances_count}, Totale: ${formatCurrency(deletePreview.totals.advances_total)})`
                    : ''}
                </Alert>
              )}

              {deletePreview.warnings?.hasInstallmentPlans && <Alert severity="warning" sx={{ mb: 2 }}>La fattura è collegata a una rateizzazione: scollegala dal piano prima di eliminarla.</Alert>}
              {deletePreview.warnings?.hasExtraInterventionsLinked && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Interventi extra collegati: verranno riportati in sospeso.
                  {deletePreview.totals?.interventions_count
                    ? ` (Interventi: ${deletePreview.totals.interventions_count}, Totale: ${formatCurrency(deletePreview.totals.interventions_total)})`
                    : ''}
                </Alert>
              )}

              {deletePreview.invoice?.billing_type === 'refill' ? <RefillRows items={deletePreview.refill_items || []}/> : <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Letture collegate ({deletePreview.meterReadings?.length || 0})
              </Typography>

              {(deletePreview.meterReadings?.length || 0) === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nessuna lettura collegata.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Contatore</TableCell>
                        <TableCell>Data lettura</TableCell>
                        <TableCell align="right">Consumo (L)</TableCell>
                        <TableCell align="right">Importo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(deletePreview.meterReadings || []).map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.meter_number || r.meter_id || '-'}</TableCell>
                          <TableCell>{formatDate(r.data_lettura)}</TableCell>
                          <TableCell align="right">{formatNumber(r.consumo_litri, 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(r.importo_totale)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              </>}
              <Alert severity="warning" sx={{ mt: 2 }}>
                {deletePreview.invoice?.billing_type === 'refill' ? 'Confermando, la fattura sarà rimossa definitivamente, anche se già annullata. I rifornimenti potranno essere fatturati nuovamente. Il progressivo non viene riutilizzato automaticamente.' : "Confermando, la fattura verrà eliminata definitivamente. Potrai riutilizzare il NUF modificando un'altra fattura."}
              </Alert>

              {deletePermanentMutation.isError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {deletePermanentMutation.error?.response?.data?.message || 'Errore durante la cancellazione'}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deletePermanentMutation.isPending}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deletePermanentMutation.mutate(selectedInvoice?.id)}
            disabled={deletePreviewLoading || !!deletePreviewError || !deletePreview || deletePermanentMutation.isPending || deletePreview?.warnings?.hasCreditNotes || ((deletePreview?.invoice?.billing_type === 'refill' || deletePreview?.warnings?.isLinkedCreditNote) && (deletePreview.warnings?.hasPayments || deletePreview.warnings?.hasAdvances || deletePreview.warnings?.hasInstallmentPlans))}
          >
            Elimina definitivamente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog invio massivo email */}
      <BulkEmailDialog 
        open={bulkEmailDialogOpen} 
        onClose={() => setBulkEmailDialogOpen(false)} 
      />

      {/* Dialog export PDF massivo */}
      <ExportPdfDialog 
        open={exportPdfDialogOpen} 
        onClose={() => setExportPdfDialogOpen(false)} 
      />

      {/* Dialog export SEPA Direct Debit */}
      <SepaExportDialog
        open={sepaDialogOpen}
        onClose={() => setSepaDialogOpen(false)}
        currentAziendaId={filterAzienda || undefined}
      />
      
      {/* Dialog invio email singola */}
      <SendEmailDialog
        open={sendEmailDialogOpen}
        onClose={() => setSendEmailDialogOpen(false)}
        invoice={selectedInvoice}
        onSuccess={() => {
          setSendSnackbar({ open: true, message: 'Email inviata con successo', severity: 'success' });
          queryClient.invalidateQueries({ queryKey: ['invoices'] });
        }}
      />
    </Box>
  );
};

export default InvoiceList;
